import React from 'react'
import { Dropdown } from 'semantic-ui-react'
import { toast } from 'react-toastify'

const SmartTagDropdown = ({ documentTypeOptions, documentType, onSelect }) => {
  // Find the document type configuration from options
  const getTypeConfig = () => {
    return documentTypeOptions.find((opt) => opt.value === documentType)
  }

  // Convert snake_case to Title Case
  const formatDisplayText = (text) => {
    return text
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
  }

  // Get display text for a key from documentTypeOptions or format it if not found
  const getDisplayText = (key) => {
    const option = documentTypeOptions.find((opt) => opt.key === key)
    return option ? option.text : formatDisplayText(key)
  }

  // Check if a path represents an array item
  const isArrayPath = (path) => {
    if (!path) return { isArray: false }

    const typeConfig = getTypeConfig()
    if (!typeConfig?.variables) return { isArray: false }

    const parts = path.split('.')
    let current = typeConfig.variables

    for (let i = 0; i < parts.length; i++) {
      if (!current[parts[i]]) break

      if (Array.isArray(current[parts[i]])) {
        return {
          isArray: true,
          arrayName: parts[i],
          itemName: parts[i].endsWith('s') ? parts[i].slice(0, -1) : parts[i],
          propertyPath: parts.slice(i + 1).join('.'),
        }
      }
      current = current[parts[i]]
    }
    return { isArray: false }
  }

  // Generate template syntax for a value
  const getTemplateSyntax = (path) => {
    if (!path) return ''

    const arrayInfo = isArrayPath(path)
    if (arrayInfo.isArray) {
      return `{% for ${arrayInfo.itemName} in ${arrayInfo.arrayName} %}
  {{ ${arrayInfo.itemName}.${arrayInfo.propertyPath} }}
{% endfor %}`
    }
    return `{{ ${path} }}`
  }

  // Recursively build dropdown options from nested object
  const buildOptions = (obj, path = '', level = 0) => {
    if (!obj) return []

    return Object.entries(obj).reduce((acc, [key, value]) => {
      const currentPath = path ? `${path}.${key}` : key
      const isTopLevel = level === 0
      const indent = (level + (isTopLevel ? 0 : 1)) * 10

      // Handle arrays
      if (Array.isArray(value)) {
        if (value.length > 0) {
          const arrayOptions = buildOptions(value[0], currentPath, level + 1)
          if (arrayOptions.length > 0) {
            acc.push({
              key: currentPath,
              text: formatDisplayText(key),
              value: null,
              disabled: true,
              content: (
                <div style={{ fontWeight: 'bold', paddingLeft: indent }}>
                  {formatDisplayText(key)} (Array)
                </div>
              ),
            })
            acc.push(...arrayOptions)
          }
        }
      }
      // Handle nested objects
      else if (typeof value === 'object' && value !== null) {
        const nestedOptions = buildOptions(value, currentPath, level + 1)
        if (nestedOptions.length > 0) {
          const displayText = isTopLevel
            ? getDisplayText(key)
            : formatDisplayText(key)
          acc.push({
            key: currentPath,
            text: displayText,
            value: null,
            disabled: true,
            content: (
              <div style={{ fontWeight: 'bold', paddingLeft: indent }}>
                {displayText}
              </div>
            ),
          })
          acc.push(...nestedOptions)
        }
      }
      // Handle leaf nodes
      else if (typeof value === 'string') {
        acc.push({
          key: currentPath,
          text: value,
          value: currentPath, // Changed to pass the full path instead of template syntax
          content: <div style={{ paddingLeft: indent }}>{value}</div>,
        })
      }
      return acc
    }, [])
  }

  const typeConfig = getTypeConfig()
  const options = typeConfig?.variables
    ? buildOptions(typeConfig.variables)
    : []

  // Handle copying to clipboard
  const handleSelect = async (value) => {
    if (!value) return // Don't process if value is null (header items)

    try {
      const templateValue = getTemplateSyntax(value)
      await navigator.clipboard.writeText(templateValue)
      toast.success('Variable copied to clipboard!')
    } catch (err) {
      toast.error('Failed to copy to clipboard')
      console.error('Clipboard write failed:', err)
    }
  }

  return (
    <Dropdown
      button
      className="icon"
      floating
      labeled
      icon="code"
      text={`Add Variable`}
      options={options}
      onChange={(_, data) => handleSelect(data.value)}
      style={{
        float: 'right',
        marginBottom: '1rem',
        width: 'auto',
        maxWidth: '400px',
        zIndex: 1000,
      }}
    />
  )
}

export default SmartTagDropdown
